"use client";

import { useState, useEffect } from "react";
import Link from "next/link";

interface GlobeNode {
  id: string;
  x: number;
  y: number;
  size: number;
  delay: number;
}

interface DataArc {
  id: string;
  startNodeId: string;
  endNodeId: string;
  delay: number;
  duration: number;
}

const globeNodes: GlobeNode[] = [
  { id: "node-1", x: 28, y: 22, size: 8, delay: 0 },
  { id: "node-2", x: 72, y: 28, size: 6, delay: 0.2 },
  { id: "node-3", x: 50, y: 48, size: 10, delay: 0.4 },
  { id: "node-4", x: 22, y: 65, size: 7, delay: 0.6 },
  { id: "node-5", x: 78, y: 68, size: 9, delay: 0.8 },
  { id: "node-6", x: 48, y: 78, size: 6, delay: 1.0 },
];

const dataArcs: DataArc[] = [
  { id: "arc-1", startNodeId: "node-1", endNodeId: "node-2", delay: 0, duration: 2.8 },
  { id: "arc-2", startNodeId: "node-2", endNodeId: "node-3", delay: 0.6, duration: 3.2 },
  { id: "arc-3", startNodeId: "node-3", endNodeId: "node-4", delay: 1.2, duration: 2.6 },
  { id: "arc-4", startNodeId: "node-3", endNodeId: "node-5", delay: 1.8, duration: 3.0 },
  { id: "arc-5", startNodeId: "node-4", endNodeId: "node-6", delay: 2.4, duration: 2.8 },
];

function getNodeById(id: string) {
  return globeNodes.find((n) => n.id === id)!;
}

function buildArcPath(startX: number, startY: number, endX: number, endY: number) {
  const midX = (startX + endX) / 2;
  const midY = (startY + endY) / 2 - 14;
  return `M ${startX} ${startY} Q ${midX} ${midY} ${endX} ${endY}`;
}

export default function HeroSection() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20,
      });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <section className="relative w-full min-h-[100dvh] flex items-center px-4 sm:px-6 md:px-12 pt-20 sm:pt-24 pb-12 sm:pb-16 overflow-hidden bg-primary">
      {/* Radial Gradient Background */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-radial from-accent/20 via-primary to-primary"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] sm:w-[800px] h-[600px] sm:h-[800px] bg-accent/10 rounded-full blur-3xl"></div>
      </div>

      <style>{`
        @keyframes arcFlow {
          0% { stroke-dashoffset: 200; opacity: 0; }
          10% { opacity: 1; }
          80% { opacity: 1; }
          100% { stroke-dashoffset: 0; opacity: 0; }
        }
        @keyframes cardFloat {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-6px); }
        }
      `}</style>

      {/* Content Container */}
      <div className="relative z-10 w-full max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-16 items-center">
        {/* Left Content */}
        <div className="flex flex-col gap-5 sm:gap-8 text-left">
          <div className="inline-flex items-center gap-2 text-secondary text-xs sm:text-sm font-semibold uppercase tracking-[1.5px] mb-0 w-fit">
            Trusted by data-driven enterprises globally
          </div>

          <h1 className="text-white text-[28px] sm:text-[34px] md:text-[44px] lg:text-[48px] font-bold leading-[1.2] tracking-[-0.5px] font-serif">
            Turn Enterprise Data Into Clear Decisions. Not Guesswork.
          </h1>

          <p className="text-white/80 text-sm sm:text-base md:text-lg leading-[1.7] max-w-xl lg:max-w-none">
            Simax Systems integrates data, people, and strategy into one clarity-focused platform,
            engineered for the modern C-suite. See every angle, predict every challenge.
          </p>

          <div className="mt-2 sm:mt-4 flex justify-start">
            <Link
              href="/contact"
              className="inline-block text-center w-full sm:w-auto min-h-[48px] bg-secondary hover:bg-secondary/90 text-primary px-6 sm:px-8 py-4 text-sm font-semibold uppercase tracking-[1.5px] transition-all rounded-md shadow-lg hover:shadow-xl active:scale-[0.98] lg:hover:scale-105 duration-300"
            >
              Schedule Diagnostic Call
            </Link>
          </div>
        </div>

        {/* Right — interactive globe with arcs & metric cards */}
        <div className="relative w-full h-[280px] sm:h-[360px] md:h-[420px] lg:h-[500px] xl:h-[600px] flex items-center justify-center min-w-0">
          <div
            className="relative w-[260px] h-[260px] sm:w-[320px] sm:h-[320px] md:w-[380px] md:h-[380px] lg:w-[500px] lg:h-[500px]"
            style={{
              transform: `perspective(1000px) rotateY(${mousePosition.x}deg) rotateX(${-mousePosition.y}deg)`,
              transition: "transform 0.3s ease-out",
            }}
          >
            {/* Main Globe Sphere */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-accent/40 via-accent/60 to-accent/80 shadow-2xl">
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100">
                <ellipse cx="50" cy="50" rx="48" ry="48" fill="none" stroke="#2E5A8F" strokeWidth="0.3" opacity="0.3" />
                <ellipse cx="50" cy="50" rx="48" ry="35" fill="none" stroke="#2E5A8F" strokeWidth="0.3" opacity="0.3" />
                <ellipse cx="50" cy="50" rx="48" ry="20" fill="none" stroke="#2E5A8F" strokeWidth="0.3" opacity="0.3" />
                <line x1="50" y1="2" x2="50" y2="98" stroke="#2E5A8F" strokeWidth="0.3" opacity="0.3" />
                <line x1="2" y1="50" x2="98" y2="50" stroke="#2E5A8F" strokeWidth="0.3" opacity="0.3" />
                <ellipse cx="50" cy="50" rx="20" ry="48" fill="none" stroke="#2E5A8F" strokeWidth="0.3" opacity="0.3" />
                <ellipse cx="50" cy="50" rx="35" ry="48" fill="none" stroke="#2E5A8F" strokeWidth="0.3" opacity="0.3" />
              </svg>
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-white/10 to-transparent"></div>
              <div className="absolute inset-0 rounded-full shadow-[inset_0_0_60px_rgba(46,90,143,0.6)]"></div>
            </div>

            {isHydrated &&
              dataArcs.map((arc) => {
                const startNode = getNodeById(arc.startNodeId);
                const endNode = getNodeById(arc.endNodeId);
                const pathD = buildArcPath(startNode.x, startNode.y, endNode.x, endNode.y);
                const arcId = `arc-path-${arc.id}`;
                const gradId = `grad-${arc.id}`;

                return (
                  <svg
                    key={arc.id}
                    className="absolute inset-0 w-full h-full pointer-events-none"
                    viewBox="0 0 100 100"
                  >
                    <defs>
                      <linearGradient
                        id={gradId}
                        gradientUnits="userSpaceOnUse"
                        x1={`${startNode.x}`}
                        y1={`${startNode.y}`}
                        x2={`${endNode.x}`}
                        y2={`${endNode.y}`}
                      >
                        <stop offset="0%" stopColor="#C5A572" stopOpacity="0.0" />
                        <stop offset="40%" stopColor="#C5A572" stopOpacity="0.7" />
                        <stop offset="100%" stopColor="#C5A572" stopOpacity="0.0" />
                      </linearGradient>
                      <path id={arcId} d={pathD} />
                    </defs>
                    <path d={pathD} fill="none" stroke="#C5A572" strokeWidth="0.3" opacity="0.15" />
                    <path
                      d={pathD}
                      fill="none"
                      stroke={`url(#${gradId})`}
                      strokeWidth="0.6"
                      strokeDasharray="200"
                      strokeDashoffset="200"
                      style={{
                        animation: `arcFlow ${arc.duration}s ease-in-out ${arc.delay}s infinite`,
                      }}
                    />
                    <circle r="1.2" fill="#C5A572" opacity="0">
                      <animateMotion
                        dur={`${arc.duration}s`}
                        begin={`${arc.delay}s`}
                        repeatCount="indefinite"
                        calcMode="spline"
                        keySplines="0.4 0 0.6 1"
                      >
                        <mpath href={`#${arcId}`} />
                      </animateMotion>
                      <animate
                        attributeName="opacity"
                        values="0;0;1;1;0"
                        keyTimes="0;0.05;0.15;0.85;1"
                        dur={`${arc.duration}s`}
                        begin={`${arc.delay}s`}
                        repeatCount="indefinite"
                      />
                    </circle>
                  </svg>
                );
              })}

            {isHydrated &&
              globeNodes.map((node) => (
                <div
                  key={node.id}
                  className="absolute"
                  style={{
                    left: `${node.x}%`,
                    top: `${node.y}%`,
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  <div
                    className="absolute bg-secondary rounded-full blur-md opacity-50"
                    style={{
                      width: `${node.size * 2}px`,
                      height: `${node.size * 2}px`,
                      transform: "translate(-50%, -50%)",
                    }}
                  />
                  <div
                    className="relative bg-secondary rounded-full shadow-lg"
                    style={{ width: `${node.size}px`, height: `${node.size}px` }}
                  >
                    <div
                      className="absolute inset-0 bg-white/40 rounded-full animate-ping"
                      style={{ animationDuration: "2.5s", animationDelay: `${node.delay}s` }}
                    />
                  </div>
                </div>
              ))}

            <div
              className="absolute inset-0 rounded-full border border-secondary/30 animate-spin-slow"
              style={{ animationDuration: "40s" }}
            />
            <div
              className="absolute inset-0 rounded-full border border-secondary/20"
              style={{ transform: "scale(1.1)" }}
            />

            {/* Metric cards — lg+ only so small viewports stay clean */}
            <div
              className="hidden lg:block absolute -right-6 top-16 bg-primary/80 backdrop-blur-md border border-secondary/30 rounded-lg p-3 shadow-xl"
              style={{ animation: `cardFloat 5s ease-in-out 0.5s infinite` }}
            >
              <div className="text-secondary text-[9px] font-bold mb-1 uppercase tracking-wider">
                Data Sources Integrated
              </div>
              <div className="text-white text-base font-bold">24 systems</div>
            </div>

            <div
              className="hidden lg:block absolute -left-6 bottom-28 bg-primary/80 backdrop-blur-md border border-secondary/30 rounded-lg p-3 shadow-xl"
              style={{ animation: `cardFloat 6s ease-in-out 1s infinite` }}
            >
              <div className="text-secondary text-[9px] font-bold mb-1 uppercase tracking-wider">
                Reports Generated
              </div>
              <div className="text-white text-base font-bold">350+ dashboards</div>
            </div>

            <div
              className="hidden lg:block absolute right-8 bottom-12 bg-primary/80 backdrop-blur-md border border-secondary/30 rounded-lg p-3 shadow-xl"
              style={{ animation: `cardFloat 5.5s ease-in-out 1.5s infinite` }}
            >
              <div className="text-secondary text-[9px] font-bold mb-1 uppercase tracking-wider">
                Data Processing
              </div>
              <div className="text-white text-base font-bold">3M records/hour</div>
            </div>

            <div
              className="hidden lg:block absolute -left-4 top-24 bg-primary/80 backdrop-blur-md border border-secondary/30 rounded-lg p-3 shadow-xl"
              style={{ animation: `cardFloat 4.8s ease-in-out 2s infinite` }}
            >
              <div className="text-secondary text-[9px] font-bold mb-1 uppercase tracking-wider">Data Sync</div>
              <div className="text-white text-base font-bold">Real-time pipelines</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
